/**
 * Colour themes and contrast.
 *
 * A theme supplies the ground, the ink and the semantic hues; everything else the tokens
 * derive (washes, edges, hairlines, the steel faces of panels and buttons) is computed from
 * those here, so a theme is a dozen colours rather than eighty. The pipeline-state colours and
 * the vendor liveries are not themed: the board, the tracker labels and the MR labels share
 * them, and an operator who learnt that pink means Human Review should not relearn it.
 *
 * Contrast moves the ink away from the ground (or towards it, below zero), darkens the ground
 * and strengthens the hairlines. It applies on top of any theme.
 *
 * Both are per browser, like the interface scale: they belong to the monitor and the eyes in
 * front of it, not to the account.
 */

import { useEffect, useState } from 'react';

const THEME_KEY = 'nightshift.theme';
const CONTRAST_KEY = 'nightshift.contrast';
const APPEARANCE_EVENT = 'nightshift-appearance';

/** Slider range, in percent. Zero is the theme as designed. */
export const CONTRAST_MIN = -50;
export const CONTRAST_MAX = 100;

interface ThemePalette {
  bg: string;
  bgDeep: string;
  surface: string;
  surface2: string;
  surface3: string;
  /** Main text; the hairlines are drawn in it. */
  fg: string;
  /** Four quieter text tiers, strongest first. Derived from fg and bg when absent. */
  quiet?: [string, string, string, string];
  /** Tops of the panel, button and hovered-button gradients. Derived when absent. */
  steel?: { face: string; hot: string; hover: string; hoverBase?: string };
  /** Attention: human waits, primary buttons, focus. Always a warm, light hue. */
  accent: string;
  accentBright: string;
  /** Text on an accent fill. */
  onAccent?: string;
  queued: string;
  queuedBright: string;
  danger: string;
  dangerText: string;
  warn: string;
  warnText: string;
  good: string;
  goodText: string;
  info: string;
  infoText: string;
}

export interface Theme {
  id: string;
  name: string;
  description: string;
  palette: ThemePalette;
}

export const THEMES: readonly Theme[] = [
  {
    id: 'nightshift',
    name: 'Nightshift',
    description: 'The original steel and amber',
    palette: {
      bg: '#0b0d10',
      bgDeep: '#08090c',
      surface: '#14181d',
      surface2: '#191e24',
      surface3: '#222830',
      fg: '#cfd6e4',
      quiet: ['#a8b2c2', '#8f98a8', '#6b7480', '#4a5260'],
      steel: { face: '#1b2128', hot: '#242b34', hover: '#2c343e', hoverBase: '#1e242c' },
      accent: '#ffb424',
      accentBright: '#ffc95a',
      onAccent: '#1a1204',
      queued: '#6366f1',
      queuedBright: '#a5b4fc',
      danger: '#ef4444',
      dangerText: '#fca5a5',
      warn: '#f59e0b',
      warnText: '#fbbf24',
      good: '#22c55e',
      goodText: '#86efac',
      info: '#3b82f6',
      infoText: '#93c5fd',
    },
  },
  {
    id: 'tokyo-night',
    name: 'Tokyo Night',
    description: "enkia's Tokyo Night",
    palette: {
      bg: '#16161e',
      bgDeep: '#111118',
      surface: '#1a1b26',
      surface2: '#1f2335',
      surface3: '#292e42',
      fg: '#c0caf5',
      quiet: ['#a9b1d6', '#8089b3', '#646d97', '#4a5277'],
      accent: '#e0af68',
      accentBright: '#ffc777',
      queued: '#9d7cd8',
      queuedBright: '#bb9af7',
      danger: '#f7768e',
      dangerText: '#ff9eae',
      warn: '#ff9e64',
      warnText: '#ffbd8f',
      good: '#9ece6a',
      goodText: '#b9e08f',
      info: '#7aa2f7',
      infoText: '#a4c0fa',
    },
  },
  {
    id: 'catppuccin-mocha',
    name: 'Catppuccin Mocha',
    description: 'Catppuccin, Mocha flavour',
    palette: {
      bg: '#181825',
      bgDeep: '#11111b',
      surface: '#1e1e2e',
      surface2: '#26263a',
      surface3: '#313244',
      fg: '#cdd6f4',
      quiet: ['#bac2de', '#a6adc8', '#7f849c', '#585b70'],
      accent: '#f9e2af',
      accentBright: '#fcecc9',
      queued: '#b4befe',
      queuedBright: '#cdd3fe',
      danger: '#f38ba8',
      dangerText: '#f7aec2',
      warn: '#fab387',
      warnText: '#fcc9a8',
      good: '#a6e3a1',
      goodText: '#c3edbf',
      info: '#89b4fa',
      infoText: '#adcbfc',
    },
  },
  {
    id: 'dracula',
    name: 'Dracula',
    description: 'Dracula by Zeno Rocha',
    palette: {
      bg: '#21222c',
      bgDeep: '#191a21',
      surface: '#282a36',
      surface2: '#2f3241',
      surface3: '#44475a',
      fg: '#f8f8f2',
      quiet: ['#d4d6e2', '#a7abc6', '#7c86b8', '#6272a4'],
      accent: '#f1fa8c',
      accentBright: '#f7fcb8',
      queued: '#bd93f9',
      queuedBright: '#d6bcfb',
      danger: '#ff5555',
      dangerText: '#ff9090',
      warn: '#ffb86c',
      warnText: '#ffcf9c',
      good: '#50fa7b',
      goodText: '#8dfcaa',
      info: '#8be9fd',
      infoText: '#b5f1fe',
    },
  },
  {
    id: 'one-dark-pro',
    name: 'One Dark Pro',
    description: "Atom's One Dark, as in One Dark Pro",
    palette: {
      bg: '#21252b',
      bgDeep: '#1b1e23',
      surface: '#282c34',
      surface2: '#2e333d',
      surface3: '#3e4451',
      fg: '#d7dae0',
      quiet: ['#abb2bf', '#8b929e', '#6b717d', '#4f5562'],
      accent: '#e5c07b',
      accentBright: '#efd5a3',
      queued: '#c678dd',
      queuedBright: '#d9a2e9',
      danger: '#e06c75',
      dangerText: '#eb9aa0',
      warn: '#d19a66',
      warnText: '#e0b78f',
      good: '#98c379',
      goodText: '#b7d6a1',
      info: '#61afef',
      infoText: '#93c8f4',
    },
  },
  {
    id: 'github-dark-dimmed',
    name: 'GitHub Dark Dimmed',
    description: "GitHub's dark dimmed theme",
    palette: {
      bg: '#1c2128',
      bgDeep: '#161a20',
      surface: '#22272e',
      surface2: '#2d333b',
      surface3: '#373e47',
      fg: '#cdd9e5',
      quiet: ['#adbac7', '#909dab', '#768390', '#545d68'],
      accent: '#daaa3f',
      accentBright: '#eac55f',
      queued: '#986ee2',
      queuedBright: '#b799f0',
      danger: '#e5534b',
      dangerText: '#f47067',
      warn: '#c69026',
      warnText: '#daaa3f',
      good: '#57ab5a',
      goodText: '#8ddb8c',
      info: '#539bf5',
      infoText: '#96d0ff',
    },
  },
  {
    id: 'nord',
    name: 'Nord',
    description: 'Nord by Arctic Ice Studio',
    palette: {
      bg: '#2a2f3a',
      bgDeep: '#242933',
      surface: '#2e3440',
      surface2: '#3b4252',
      surface3: '#434c5e',
      fg: '#eceff4',
      quiet: ['#d8dee9', '#b4bccb', '#8e97a8', '#677185'],
      accent: '#ebcb8b',
      accentBright: '#f2dcae',
      queued: '#b48ead',
      queuedBright: '#cbb0c6',
      danger: '#bf616a',
      dangerText: '#d8939a',
      warn: '#d08770',
      warnText: '#e0aa99',
      good: '#a3be8c',
      goodText: '#c1d5b0',
      info: '#88c0d0',
      infoText: '#b0d6e1',
    },
  },
  {
    id: 'rose-pine',
    name: 'Rosé Pine',
    description: 'Rosé Pine',
    palette: {
      bg: '#191724',
      bgDeep: '#13111d',
      surface: '#1f1d2e',
      surface2: '#26233a',
      surface3: '#403d52',
      fg: '#e0def4',
      quiet: ['#c4c1dc', '#908caa', '#7a7694', '#6e6a86'],
      accent: '#f6c177',
      accentBright: '#f9d4a1',
      queued: '#c4a7e7',
      queuedBright: '#d7c3ef',
      danger: '#eb6f92',
      dangerText: '#f29db5',
      warn: '#ea9a97',
      warnText: '#f0b9b7',
      good: '#3e8fb0',
      goodText: '#9ccfd8',
      info: '#9ccfd8',
      infoText: '#bfe0e6',
    },
  },
  {
    id: 'gruvbox-dark',
    name: 'Gruvbox Dark',
    description: 'Gruvbox by morhetz',
    palette: {
      bg: '#1d2021',
      bgDeep: '#171919',
      surface: '#282828',
      surface2: '#32302f',
      surface3: '#3c3836',
      fg: '#ebdbb2',
      quiet: ['#d5c4a1', '#bdae93', '#928374', '#665c54'],
      accent: '#fabd2f',
      accentBright: '#fcd36b',
      queued: '#d3869b',
      queuedBright: '#e2aab9',
      danger: '#fb4934',
      dangerText: '#fc8576',
      warn: '#fe8019',
      warnText: '#feaa63',
      good: '#b8bb26',
      goodText: '#d2d45f',
      info: '#83a598',
      infoText: '#a9c2b8',
    },
  },
];

const DEFAULT_THEME_ID = 'nightshift';

export function themeById(id: string): Theme {
  return THEMES.find((theme) => theme.id === id) ?? THEMES[0]!;
}

/* ── colour maths ──────────────────────────────────────────────────────────────────── */

type Rgb = [number, number, number];

function parseHex(hex: string): Rgb {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? [...value].map((c) => c + c).join('') : value;
  return [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16)) as Rgb;
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')}`;
}

/** `a` moved towards `b` by `t` (0 is `a`, 1 is `b`), in sRGB. */
function mix(a: string, b: string, t: number): string {
  const x = parseHex(a);
  const y = parseHex(b);
  return toHex([0, 1, 2].map((i) => x[i]! + (y[i]! - x[i]!) * t) as Rgb);
}

function alpha(hex: string, a: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.round(Math.min(1, Math.max(0, a)) * 1000) / 1000})`;
}

function luminance(hex: string): number {
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = parseHex(hex).map(channel) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two opaque colours. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/* ── resolution ────────────────────────────────────────────────────────────────────── */

export function clampContrast(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(Math.min(CONTRAST_MAX, Math.max(CONTRAST_MIN, value)));
}

/**
 * Every theme-dependent token for a theme at a contrast, as CSS custom properties.
 *
 * Above zero, ink moves towards white (the quiet tiers furthest, since they are the ones that
 * fail first), the ground darkens and hairlines strengthen. Below zero the ink settles towards
 * the ground and the hairlines fade.
 */
export function themeVariables(theme: Theme, contrastPercent: number): Record<string, string> {
  const c = clampContrast(contrastPercent) / 100;
  const p = theme.palette;
  const quiet = p.quiet ?? [mix(p.fg, p.bg, 0.2), mix(p.fg, p.bg, 0.35), mix(p.fg, p.bg, 0.5), mix(p.fg, p.bg, 0.65)];
  const steel = p.steel ?? { face: mix(p.surface, p.fg, 0.04), hot: mix(p.surface2, p.fg, 0.06), hover: mix(p.surface2, p.fg, 0.1) };

  const ground = (hex: string, weight: number): string => (c > 0 ? mix(hex, '#000000', c * weight) : hex);
  const ink = (hex: string, weight: number): string => (c >= 0 ? mix(hex, '#ffffff', c * weight) : mix(hex, p.bg, -c * weight));
  const tint = (hex: string): string => ink(hex, 0.35);

  const bg = ground(p.bg, 0.4);
  const bgDeep = ground(p.bgDeep, 0.4);
  const surface = ground(p.surface, 0.3);
  const surface2 = ground(p.surface2, 0.3);
  const surface3 = ground(p.surface3, 0.25);
  const text = ink(p.fg, 0.5);
  const quietInk = quiet.map((hex, index) => ink(hex, [0.5, 0.6, 0.6, 0.55][index]!));
  const lines = c >= 0 ? 1 + 1.4 * c : 1 + 0.6 * c;
  const face = ground(steel.face, 0.3);
  const hot = ground(steel.hot, 0.3);
  const hover = ground(steel.hover, 0.25);
  const hoverBase = ground(steel.hoverBase ?? mix(p.surface2, p.fg, 0.04), 0.3);

  return {
    '--bg': bg,
    '--bg-deep': bgDeep,
    '--surface': surface,
    '--surface-2': surface2,
    '--surface-3': surface3,
    '--hairline': alpha(text, 0.1 * lines),
    '--hairline-strong': alpha(text, 0.18 * lines),
    '--steel-face': `linear-gradient(180deg, ${face} 0%, ${surface} 100%)`,
    '--steel-face-hot': `linear-gradient(180deg, ${hot} 0%, ${surface2} 100%)`,
    '--steel-face-hover': `linear-gradient(180deg, ${hover} 0%, ${hoverBase} 100%)`,
    '--engrave': quietInk[1]!,
    '--text': text,
    '--text-2': quietInk[0]!,
    '--text-3': quietInk[1]!,
    '--text-4': quietInk[2]!,
    '--text-5': quietInk[3]!,
    '--accent': p.accent,
    '--accent-bright': p.accentBright,
    '--accent-dim': mix(p.accent, p.bg, 0.35),
    '--accent-wash': alpha(p.accent, 0.14),
    '--accent-edge': alpha(p.accent, 0.42),
    '--on-accent': p.onAccent ?? mix(p.accent, '#000000', 0.88),
    '--queued': p.queued,
    '--queued-bright': tint(p.queuedBright),
    '--queued-wash': alpha(p.queued, 0.14),
    '--queued-edge': alpha(p.queued, 0.42),
    '--danger': p.danger,
    '--danger-text': tint(p.dangerText),
    '--danger-wash': alpha(p.danger, 0.12),
    '--danger-edge': alpha(p.danger, 0.34),
    '--warn': p.warn,
    '--warn-text': tint(p.warnText),
    '--warn-wash': alpha(p.warn, 0.12),
    '--warn-edge': alpha(p.warn, 0.34),
    '--good': p.good,
    '--good-text': tint(p.goodText),
    '--good-wash': alpha(p.good, 0.12),
    '--info': p.info,
    '--info-text': tint(p.infoText),
  };
}

/* ── persistence ───────────────────────────────────────────────────────────────────── */

export interface Appearance {
  themeId: string;
  contrast: number;
}

function appearance(): Appearance {
  try {
    const themeId = window.localStorage.getItem(THEME_KEY) ?? DEFAULT_THEME_ID;
    const contrast = Number(window.localStorage.getItem(CONTRAST_KEY) ?? 0);
    return { themeId: themeById(themeId).id, contrast: clampContrast(contrast) };
  } catch {
    return { themeId: DEFAULT_THEME_ID, contrast: 0 };
  }
}

export function setAppearance(next: Appearance): void {
  const resolved = { themeId: themeById(next.themeId).id, contrast: clampContrast(next.contrast) };
  try {
    window.localStorage.setItem(THEME_KEY, resolved.themeId);
    window.localStorage.setItem(CONTRAST_KEY, String(resolved.contrast));
  } catch {
    // Applies for this page lifetime; it simply will not persist.
  }
  applyAppearance(resolved);
  window.dispatchEvent(new Event(APPEARANCE_EVENT));
}

/**
 * Write the tokens onto the document root. The original theme at zero contrast clears them
 * instead, so tokens.css stays the single source for the default look.
 */
export function applyAppearance(current: Appearance = appearance()): void {
  const root = document.documentElement;
  const variables = themeVariables(themeById(DEFAULT_THEME_ID), 0);
  if (current.themeId === DEFAULT_THEME_ID && current.contrast === 0) {
    for (const name of Object.keys(variables)) root.style.removeProperty(name);
  } else {
    for (const [name, value] of Object.entries(themeVariables(themeById(current.themeId), current.contrast))) root.style.setProperty(name, value);
  }
  root.dataset.theme = themeById(current.themeId).id;
}

export function useAppearance(): Appearance {
  const [current, setCurrent] = useState(appearance);
  useEffect(() => {
    const sync = (): void => setCurrent(appearance());
    // Another tab of this browser changed it: repaint this one too.
    const fromStorage = (event: StorageEvent): void => {
      if (event.key !== THEME_KEY && event.key !== CONTRAST_KEY) return;
      applyAppearance();
      sync();
    };
    window.addEventListener(APPEARANCE_EVENT, sync);
    window.addEventListener('storage', fromStorage);
    return () => {
      window.removeEventListener(APPEARANCE_EVENT, sync);
      window.removeEventListener('storage', fromStorage);
    };
  }, []);
  return current;
}
